import React, { useState, useEffect } from 'react';
import { get, post, put } from '../utils/api';
import SupplierQuotations from '../components/SupplierQuotations';
import { useAuth } from '../context/AuthContext';

const STATUS_COLORS = {
  pending:             { bg:'#FFF3DC', color:'#8B5E0A' },
  inventory_verified:  { bg:'#EDF4FF', color:'#1E4FAD' },
  approved:            { bg:'#E8F5EB', color:'#2E6B3A' },
  rejected:            { bg:'#FDEAEA', color:'#8F2E24' },
  fulfilled:           { bg:'#F2EFE7', color:'#556B2F' },
};

const PRIORITIES = ['low','normal','high','urgent'];
const fmt = n => '₦' + Number(n||0).toLocaleString('en-NG');

const ALLOWED_SUBMITTERS = [
  'super_admin','sales_manager',
  'inventory_officer','procurement_logistics_officer','front_desk_officer'
];

export default function PurchaseRequests() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const canSubmit = ALLOWED_SUBMITTERS.includes(user?.role);
  const canVerifyInventory = ['super_admin','inventory_officer'].includes(user?.role);
  const canFulfil = ['super_admin','procurement_logistics_officer'].includes(user?.role);

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [jobOrders, setJobOrders] = useState([]);
  const [materials,setMaterials]=useState([]);
  // PURCHASE SCOPE AND JOB ORDER CONTROL
  const [form, setForm] = useState({
    title:'',
    reason:'',
    required_date:'',
    priority:'normal',
    purchase_scope:'production',
    job_order_id:'',
    current_stock_note:''
  });
  const [items, setItems] = useState([{description:'',quantity:1,unit:'',estimated_cost:'',specification:''}]);
  const [saving, setSaving] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalForm, setApprovalForm] = useState({ bank_account_id:'', approval_notes:'', approved_items:[] });
  const [bankAccounts, setBankAccounts] = useState([]);
  const [showFulfilModal, setShowFulfilModal] = useState(false);
  const [fulfilForm, setFulfilForm] = useState({ actual_cost:'', receipt_reference:'' });
  const [inventoryNote, setInventoryNote] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [showReject, setShowReject] = useState(false);
  const [showApprove, setShowApprove] = useState(false);
  const [showVerify, setShowVerify] = useState(false);

  const load = () => {
    setLoading(true);
    get('/bank-accounts').then(d => setBankAccounts(d.data||[]));
    Promise.all([
      get('/purchase-requests'),
      get('/materials?include_supplies=true'),
      get(
        '/job-orders?status=' +
        'pending,confirmed,in_production,' +
        'printing,quality_check'
      )
    ]).then(([pr, ma, jo]) => {
      setMaterials((ma.data||[]).filter(x=>x.is_active));
      setRequests(pr.data||[]);
      setJobOrders(jo.data||[]);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const filtered = requests.filter(r => activeTab === 'all' || r.status === activeTab);
  const pendingCount = requests.filter(r => r.status === 'pending').length;

  const addItem = () => setItems([...items, {description:'',quantity:1,unit:'',estimated_cost:'',specification:''}]);
  const removeItem = i => setItems(items.filter((_,idx)=>idx!==i));
  const updateItem = (i, field, val) => {
    const updated = [...items];
    updated[i] = {...updated[i],[field]:val};
    if(field==='material_id'){const m=materials.find(x=>x.id===val);updated[i]={...updated[i],description:m?.name||'',unit:m?.unit_of_measure||''};}
    setItems(updated);
  };

  const totalEstimated = items.reduce((sum,item) =>
    sum+(parseFloat(item.estimated_cost||0)*parseFloat(item.quantity||1)), 0);

  const submitRequest = async () => {
    if (!form.title) {
      alert('Title required');
      return;
    }

    if (
      form.purchase_scope === 'production' &&
      !form.job_order_id
    ) {
      alert(
        'Select the job order requiring this purchase'
      );
      return;
    }

    if (items.some(x=>!x.material_id)) {
      alert('Add at least one item');
      return;
    }

    if(form.purchase_scope==='production'&&items.some(i=>materials.find(m=>m.id===i.material_id)?.is_supply)){alert('Supplies purchases must use General Stock.');return;}
    setSaving(true);
    try {
      await post('/purchase-requests', {...form, items});
      setShowForm(false);
      setForm({
        title:'',
        reason:'',
        required_date:'',
        priority:'normal',
        purchase_scope:'production',
        job_order_id:'',
        current_stock_note:''
      });
      setItems([{description:'',quantity:1,unit:'',estimated_cost:'',specification:''}]);
      load();
    } catch(err) { alert(err.message); }
    finally { setSaving(false); }
  };

  const verifyInventory = async () => {
    setSaving(true);
    try {
      await put('/purchase-requests/'+selected.id+'/verify-inventory', {inventory_notes: inventoryNote});
      setShowVerify(false);
      setInventoryNote('');
      load();
      setSelected(null);
    } catch(err) { alert(err.message); }
    finally { setSaving(false); }
  };

  const approve = async () => {
    setSaving(true);
    try {
      await put('/purchase-requests/'+selected.id+'/approve', {});
      setShowApprove(false);
      load();
      setSelected(null);
    } catch(err) { alert(err.message); }
    finally { setSaving(false); }
  };

  const reject = async () => {
    if (!rejectReason) { alert('Rejection reason required'); return; }
    setSaving(true);
    try {
      await put('/purchase-requests/'+selected.id+'/reject', {rejection_reason: rejectReason});
      setShowReject(false);
      setRejectReason('');
      load();
      setSelected(null);
    } catch(err) { alert(err.message); }
    finally { setSaving(false); }
  };

  const fulfil = async () => {
    if (!window.confirm('Mark this purchase request as fulfilled?')) return;
    try {
      await put('/purchase-requests/'+selected.id+'/fulfil', {});
      load();
      setSelected(null);
    } catch(err) { alert(err.message); }
  };

  return (
    <div>
      <div className="flex-between mb-lg">
        <div>
          <h2 className="page-title">🛒 Purchase Requests</h2>
          <p className="text-muted text-sm" style={{marginTop:4}}>
            Request items for procurement approval
          </p>
        </div>
        {canSubmit && (
          <button className="btn btn-primary" onClick={()=>setShowForm(true)}>
            + New Request
          </button>
        )}
      </div>

      {/* Tabs */}
      <div style={{display:'flex',gap:6,marginBottom:20,borderBottom:'1px solid var(--border-light)',paddingBottom:12}}>
        {[['all','All'],['pending','Pending'],['inventory_verified','Verified'],
          ['approved','Approved'],['fulfilled','Fulfilled'],['rejected','Rejected']
        ].map(([key,label])=>(
          <button key={key} className={'btn btn-sm '+(activeTab===key?'btn-primary':'btn-ghost')}
            onClick={()=>setActiveTab(key)}>
            {label}
            {key==='pending' && pendingCount > 0 && (
              <span style={{marginLeft:6,background:'#8F2E24',color:'#fff',
                borderRadius:10,padding:'0 6px',fontSize:10}}>{pendingCount}</span>
            )}
          </button>
        ))}
      </div>

      {loading ? <div className="spinner"></div> : filtered.length === 0 ? (
        <div className="empty-state">
          <h3>No purchase requests</h3>
          {canSubmit && <p>Click + New Request to submit one</p>}
        </div>
      ) : (
        <div style={{display:'flex',flexDirection:'column',gap:10}}>
          {filtered.map(r => {
            const sc = STATUS_COLORS[r.status]||{};
            return (
              <div key={r.id} style={{background:'#fff',border:'1px solid var(--border-light)',
                borderRadius:10,padding:16,cursor:'pointer'}}
                onClick={()=>setSelected(r)}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                  <div style={{flex:1}}>
                    <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
                      <span style={{fontFamily:'monospace',fontSize:11,color:'#8A8A8A'}}>{r.request_number}</span>
                      <span style={{fontSize:9,fontWeight:700,padding:'2px 8px',borderRadius:20,
                        textTransform:'uppercase',...sc}}>{r.status?.replace('_',' ')}</span>
                      {r.priority !== 'normal' && (
                        <span style={{fontSize:9,fontWeight:700,padding:'2px 8px',borderRadius:20,
                          background:r.priority==='urgent'?'#FDEAEA':'#FFF3DC',
                          color:r.priority==='urgent'?'#8F2E24':'#8B5E0A',
                          textTransform:'uppercase'}}>{r.priority}</span>
                      )}
                    </div>
                    <div style={{fontSize:14,fontWeight:600,color:'#3B2A21',marginBottom:4}}>{r.title}</div>
                    <div style={{fontSize:11,color:'#8A8A8A'}}>
                      By {r.submitted_by_name} · {new Date(r.created_at).toLocaleDateString('en-NG')}
                      {r.required_date && <span> · Needed by: {r.required_date}</span>}
                      {r.total_estimated > 0 && <span> · Est: {fmt(r.total_estimated)}</span>}
                      {r.job_number && <span> · Job: {r.job_number}</span>}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* New Request Form */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:620}}>
            <div className="modal-hdr">
              <span className="modal-title">New Purchase Request</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowForm(false)}>X</button>
            </div>
            <div className="modal-body" style={{maxHeight:'75vh',overflowY:'auto'}}>
              <div className="form-row">
                <div className="form-group" style={{flex:2}}>
                  <label className="form-label">Title *</label>
                  <input className="form-control" value={form.title}
                    onChange={e=>setForm({...form,title:e.target.value})}
                    placeholder="e.g. Jute material for August orders" />
                </div>
                <div className="form-group">
                  <label className="form-label">Priority</label>
                  <select className="form-control" value={form.priority}
                    onChange={e=>setForm({...form,priority:e.target.value})}>
                    {PRIORITIES.map(p=><option key={p} value={p}>{p.charAt(0).toUpperCase()+p.slice(1)}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    Required By Date
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    value={form.required_date}
                    onChange={e =>
                      setForm({
                        ...form,
                        required_date:
                          e.target.value
                      })
                    }
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Purchase Type *
                  </label>
                  <select
                    className="form-control"
                    value={form.purchase_scope}
                    onChange={e => {
                      const purchaseScope =
                        e.target.value;

                      setForm({
                        ...form,
                        purchase_scope:
                          purchaseScope,
                        job_order_id:
                          purchaseScope ===
                            'production'
                            ? form.job_order_id
                            : ''
                      });
                    }}
                  >
                    <option value="production">
                      Production Job
                    </option>
                    <option value="general_stock">
                      General Stock
                    </option>
                  </select>
                </div>
              </div>

              {form.purchase_scope ===
                'production' && (
                <div className="form-group">
                  <label className="form-label">
                    Job Order *
                  </label>
                  <select
                    className="form-control"
                    value={form.job_order_id}
                    onChange={e =>
                      setForm({
                        ...form,
                        job_order_id:
                          e.target.value
                      })
                    }
                    required
                  >
                    <option value="">
                      Select an open job order
                    </option>

                    {jobOrders.map(jo => {
                      const details = [
                        jo.job_number,
                        jo.customer_name,
                        jo.product_description,
                        jo.delivery_date
                          ? `Due ${jo.delivery_date}`
                          : null
                      ]
                        .filter(Boolean)
                        .join(' — ');

                      return (
                        <option
                          key={jo.id}
                          value={jo.id}
                        >
                          {details}
                        </option>
                      );
                    })}
                  </select>

                  {jobOrders.length === 0 && (
                    <div
                      style={{
                        marginTop:6,
                        color:'#8F2E24',
                        fontSize:11
                      }}
                    >
                      No open job orders are
                      currently available.
                    </div>
                  )}
                </div>
              )}

              {form.purchase_scope ===
                'general_stock' && (
                <div
                  style={{
                    background:'#F2EFE7',
                    borderRadius:8,
                    padding:10,
                    marginBottom:14,
                    color:'#556B2F',
                    fontSize:11
                  }}
                >
                  General-stock purchases are
                  not attached to a customer job.
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Reason / Justification</label>
                <textarea className="form-control" rows={2} value={form.reason}
                  onChange={e=>setForm({...form,reason:e.target.value})}
                  placeholder="Why is this purchase needed?" />
              </div>
              <div className="form-group">
                <label className="form-label">Current Stock Note</label>
                <input className="form-control" value={form.current_stock_note}
                  onChange={e=>setForm({...form,current_stock_note:e.target.value})}
                  placeholder="e.g. Current jute stock: 50m, reorder level: 100m" />
              </div>

              {/* Items */}
              <div style={{marginBottom:16}}>
                <div style={{fontSize:10,fontWeight:600,color:'#556B2F',textTransform:'uppercase',
                  letterSpacing:'.08em',marginBottom:8}}>Items *</div>
                {items.map((item,i)=>(
                  <div key={i} style={{background:'#F9F9F9',borderRadius:8,padding:12,marginBottom:8}}>
                    <div style={{display:'grid',gridTemplateColumns:'2fr 1fr 1fr 1fr',gap:8,marginBottom:6}}>
                      <select className="form-control" value={item.material_id||''} onChange={e=>updateItem(i,'material_id',e.target.value)}><option value="">Select registered parent material</option>{materials.map(m=><option key={m.id} value={m.id}>{m.material_code} — {m.name} ({m.unit_of_measure}){m.is_supply ? ' — Supplies / Spares' : ''}</option>)}</select>
                      <input type="number" className="form-control" value={item.quantity}
                        onChange={e=>updateItem(i,'quantity',e.target.value)}
                        placeholder="Qty" min="1" style={{fontSize:12}} />
                      <input className="form-control" value={item.unit}
                        readOnly
                        placeholder="Unit" style={{fontSize:12}} />
                      <input type="number" className="form-control" value={item.estimated_cost}
                        onChange={e=>updateItem(i,'estimated_cost',e.target.value)}
                        placeholder="Est. cost" style={{fontSize:12}} />
                    </div>
                    <div style={{display:'flex',gap:8,alignItems:'center'}}>
                      <input className="form-control" value={item.specification}
                        onChange={e=>updateItem(i,'specification',e.target.value)}
                        placeholder="Specification / description" style={{fontSize:12,flex:1}} />
                      {items.length > 1 && (
                        <button className="btn btn-ghost btn-sm" style={{color:'#8F2E24'}}
                          onClick={()=>removeItem(i)}>✕</button>
                      )}
                    </div>
                  </div>
                ))}
                <button className="btn btn-outline btn-sm" onClick={addItem}>+ Add Item</button>
                {totalEstimated > 0 && (
                  <div style={{textAlign:'right',fontSize:13,fontWeight:600,color:'#556B2F',marginTop:8}}>
                    Total Estimated: {fmt(totalEstimated)}
                  </div>
                )}
              </div>
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setShowForm(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={submitRequest} disabled={saving}>
                {saving?'Submitting...':'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {selected && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:580}}>
            <div className="modal-hdr">
              <div>
                <span className="modal-title">{selected.request_number}</span>
                <span style={{marginLeft:10,fontSize:9,fontWeight:700,padding:'2px 8px',
                  borderRadius:20,textTransform:'uppercase',
                  ...(STATUS_COLORS[selected.status]||{})}}>
                  {selected.status?.replace('_',' ')}
                </span>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={()=>{setSelected(null);setShowReject(false);setShowApprove(false);setShowVerify(false);}}>X</button>
            </div>
            <div className="modal-body">
              <h3 style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.2rem',
                color:'#3B2A21',marginBottom:4}}>{selected.title}</h3>
              <div style={{fontSize:11,color:'#8A8A8A',marginBottom:12}}>
                Submitted by {selected.submitted_by_name} · {new Date(selected.created_at).toLocaleDateString('en-NG')}
                {selected.required_date && <span> · Needed by: {selected.required_date}</span>}
              </div>

              <div
                style={{
                  display:'flex',
                  flexWrap:'wrap',
                  gap:8,
                  marginBottom:12
                }}
              >
                <span
                  style={{
                    background:'#F2EFE7',
                    color:'#556B2F',
                    borderRadius:20,
                    padding:'4px 10px',
                    fontSize:10,
                    fontWeight:700,
                    textTransform:'uppercase'
                  }}
                >
                  {selected.purchase_scope ===
                    'production'
                    ? 'Production Job'
                    : 'General Stock'}
                </span>

                {selected.job_number && (
                  <span
                    style={{
                      background:'#EDF4FF',
                      color:'#1E4FAD',
                      borderRadius:20,
                      padding:'4px 10px',
                      fontSize:10,
                      fontWeight:700
                    }}
                  >
                    Job: {selected.job_number}
                  </span>
                )}
              </div>

              {selected.reason && (
                <div style={{background:'#F2EFE7',borderRadius:8,padding:12,marginBottom:12,fontSize:13}}>
                  {selected.reason}
                </div>
              )}

              {selected.current_stock_note && (
                <div style={{fontSize:12,color:'#556B2F',marginBottom:12}}>
                  📦 Stock note: {selected.current_stock_note}
                </div>
              )}

              {/* Items */}
              {(() => {
                const items = typeof selected.items === 'string'
                  ? JSON.parse(selected.items||'[]') : (selected.items||[]);
                return items.length > 0 ? (
                  <div style={{marginBottom:16}}>
                    <div style={{fontSize:10,fontWeight:600,color:'#556B2F',
                      textTransform:'uppercase',letterSpacing:'.08em',marginBottom:8}}>Items</div>
                    <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                      <thead><tr style={{background:'#F2EFE7'}}>
                        {['Description','Qty','Unit','Est. Cost'].map(h=>(
                          <th key={h} style={{padding:'6px 10px',textAlign:'left',fontSize:10}}>{h}</th>
                        ))}
                      </tr></thead>
                      <tbody>
                        {items.map((item,i)=>(
                          <tr key={i} style={{borderBottom:'1px solid #F2EFE7'}}>
                            <td style={{padding:'6px 10px'}}>
                              {item.description}
                              {item.specification && <div style={{fontSize:10,color:'#8A8A8A'}}>{item.specification}</div>}
                            </td>
                            <td style={{padding:'6px 10px'}}>{item.quantity}</td>
                            <td style={{padding:'6px 10px',color:'#8A8A8A'}}>{item.unit}</td>
                            <td style={{padding:'6px 10px'}}>
                              {item.estimated_cost ? fmt(parseFloat(item.estimated_cost)*parseFloat(item.quantity||1)) : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {selected.total_estimated > 0 && (
                      <div style={{textAlign:'right',fontSize:13,fontWeight:600,
                        color:'#556B2F',marginTop:8}}>
                        Total Estimated: {fmt(selected.total_estimated)}
                      </div>
                    )}
                  </div>
                ) : null;
              })()}

              {/* Inventory verification */}
              {selected.inventory_notes && (
                <div style={{background:'#EDF4FF',borderRadius:8,padding:12,marginBottom:12}}>
                  <div style={{fontSize:11,fontWeight:600,color:'#1E4FAD',marginBottom:4}}>
                    📦 Inventory Verification
                  </div>
                  <div style={{fontSize:12}}>{selected.inventory_notes}</div>
                  <div style={{fontSize:10,color:'#8A8A8A',marginTop:4}}>
                    By {selected.inventory_verified_by_name} on {new Date(selected.inventory_verified_at).toLocaleDateString('en-NG')}
                  </div>
                </div>
              )}

              {/* Rejection */}
              {/* Supplier Quotations */}
              {['approved','inventory_verified','pending'].includes(selected.status) && (
                <SupplierQuotations
                  purchaseRequestId={selected.id}
                  items={(() => {
                    try { return typeof selected.items === 'string'
                      ? JSON.parse(selected.items||'[]') : (selected.items||[]);
                    } catch(e) { return []; }
                  })()}
                />
              )}

              {selected.status === 'rejected' && (
                <div style={{background:'#FDEAEA',borderRadius:8,padding:12,marginBottom:12}}>
                  <div style={{fontSize:11,fontWeight:600,color:'#8F2E24',marginBottom:4}}>❌ Rejected</div>
                  <div style={{fontSize:12}}>{selected.rejection_reason}</div>
                </div>
              )}

              {/* Inventory verify form */}
              {canVerifyInventory && selected.status==='pending' && showVerify && (
                <div style={{background:'#EDF4FF',border:'1px solid #B0C8F0',borderRadius:8,padding:12,marginBottom:12}}>
                  <div style={{fontSize:11,fontWeight:600,color:'#1E4FAD',marginBottom:8}}>Inventory Verification</div>
                  <div className="form-group">
                    <label className="form-label">Stock Verification Notes *</label>
                    <textarea className="form-control" rows={3} value={inventoryNote}
                      onChange={e=>setInventoryNote(e.target.value)}
                      placeholder="Current stock levels, reorder recommendation..." />
                  </div>
                  <div style={{display:'flex',gap:8}}>
                    <button className="btn btn-outline btn-sm" onClick={()=>setShowVerify(false)}>Cancel</button>
                    <button className="btn btn-primary btn-sm" onClick={verifyInventory} disabled={saving}>
                      {saving?'Saving...':'Submit Verification'}
                    </button>
                  </div>
                </div>
              )}

              {/* Reject form */}
              {isSuperAdmin && ['pending','inventory_verified'].includes(selected.status) && showReject && (
                <div style={{background:'#FDEAEA',border:'1px solid #F5B8B8',borderRadius:8,padding:12,marginBottom:12}}>
                  <div style={{fontSize:11,fontWeight:600,color:'#8F2E24',marginBottom:8}}>Reject Request</div>
                  <div className="form-group">
                    <label className="form-label">Reason *</label>
                    <textarea className="form-control" rows={2} value={rejectReason}
                      onChange={e=>setRejectReason(e.target.value)}
                      placeholder="Why is this being rejected?" />
                  </div>
                  <div style={{display:'flex',gap:8}}>
                    <button className="btn btn-outline btn-sm" onClick={()=>setShowReject(false)}>Cancel</button>
                    <button className="btn btn-danger btn-sm" onClick={reject} disabled={saving}>
                      {saving?'Rejecting...':'Confirm Rejection'}
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-ftr">
              <div style={{flex:1}}></div>
              {canVerifyInventory && selected.status==='pending' && !showVerify && (
                <button className="btn btn-outline btn-sm" style={{color:'#1E4FAD',borderColor:'#1E4FAD'}}
                  onClick={()=>{setShowVerify(true);setShowReject(false);setShowApprove(false);}}>
                  📦 Verify Stock
                </button>
              )}
              {isSuperAdmin && ['pending','inventory_verified'].includes(selected.status) && !showReject && (
                <>
                  <button className="btn btn-outline btn-sm" style={{color:'#8F2E24',borderColor:'#8F2E24'}}
                    onClick={()=>{setShowReject(true);setShowVerify(false);setShowApprove(false);}}>
                    ✗ Reject
                  </button>
                  <button className="btn btn-primary btn-sm" style={{background:'#2E6B3A',border:'none'}}
                    onClick={() => {
                        if (
                          window.confirm(
                            'Approve this purchase request?'
                          )
                        ) {
                          approve();
                        }
                      }} disabled={saving}>
                    ✓ Approve
                  </button>
                </>
              )}
              {canFulfil && selected.status==='approved' && (
                <button className="btn btn-primary btn-sm" onClick={fulfil}>
                  Mark Fulfilled
                </button>
              )}
              <button className="btn btn-outline" onClick={()=>{setSelected(null);setShowReject(false);setShowApprove(false);setShowVerify(false);}}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
